import { useCallback, useMemo, useState } from "react";

import {
  DEFAULT_ANCHOR,
  SCENARIOS,
  TOTAL_DAYS,
  buildMarkets,
  scenariosForDay,
} from "./data";
import {
  applyDecision,
  applyMarketDrift,
  emptyRun,
  finalizeRun,
  resolveDecision,
} from "./engine";
import { useLivePrices } from "./useLivePrices";
import type { GameMode, RunResult, Scenario, ScenarioOption } from "./types";

export type GamePhase = "start" | "playing" | "finished";

interface GameState {
  phase: GamePhase;
  /** The mode the player is actively "living" (their primary run). */
  mode: GameMode;
  scenarioIndex: number;
  /** Both runs share the same decisions so results are comparable. */
  bch: RunResult;
  btc: RunResult;
}

function initialState(mode: GameMode = "BCH"): GameState {
  return {
    phase: "start",
    mode,
    scenarioIndex: 0,
    bch: emptyRun("BCH"),
    btc: emptyRun("BTC"),
  };
}

export function useGame() {
  const [state, setState] = useState<GameState>(() => initialState());
  const pricesQuery = useLivePrices();

  // Anchor the week's prices to the live day-1 price when available.
  // Gameplay is all in USD terms, so the anchor only affects displayed rates
  // and the starting-balance coin conversion — the drift math is
  // anchor-independent (ratios cancel).
  const anchor = pricesQuery.data
    ? { BCH: pricesQuery.data.BCH, BTC: pricesQuery.data.BTC }
    : DEFAULT_ANCHOR;

  const markets = useMemo(
    () => buildMarkets(anchor),
    [anchor.BCH, anchor.BTC],
  );

  const priceStatus: "loading" | "live" | "fallback" = pricesQuery.isLoading
    ? "loading"
    : pricesQuery.data?.live
      ? "live"
      : "fallback";

  const currentScenario: Scenario | undefined = SCENARIOS[state.scenarioIndex];
  const currentDay = currentScenario?.day ?? TOTAL_DAYS;

  const start = useCallback((mode: GameMode) => {
    setState({ ...initialState(mode), phase: "playing" });
  }, []);

  const restart = useCallback(() => {
    setState(initialState(state.mode));
  }, [state.mode]);

  const choose = useCallback((option: ScenarioOption) => {
    setState((prev) => {
      const scenario = SCENARIOS[prev.scenarioIndex];
      if (!scenario) return prev;

      // Resolve the SAME chosen option for both modes.
      const bchDecision = resolveDecision(scenario, option, "BCH");
      const btcDecision = resolveDecision(scenario, option, "BTC");

      let bch = applyDecision(prev.bch, bchDecision);
      let btc = applyDecision(prev.btc, btcDecision);

      const nextIndex = prev.scenarioIndex + 1;
      const nextScenario = SCENARIOS[nextIndex];

      // Apply market drift when the day changes.
      if (nextScenario && nextScenario.day !== scenario.day) {
        bch = applyMarketDrift(bch, scenario.day, nextScenario.day);
        btc = applyMarketDrift(btc, scenario.day, nextScenario.day);
      }

      const finished = nextIndex >= SCENARIOS.length;

      if (finished) {
        return {
          ...prev,
          phase: "finished",
          scenarioIndex: nextIndex,
          bch: finalizeRun(bch),
          btc: finalizeRun(btc),
        };
      }

      return {
        ...prev,
        scenarioIndex: nextIndex,
        bch,
        btc,
      };
    });
  }, []);

  const progress = useMemo(() => {
    const dayScenarios = scenariosForDay(currentDay);
    const indexWithinDay = dayScenarios.findIndex(
      (s) => s.id === currentScenario?.id,
    );
    return {
      day: currentDay,
      totalDays: TOTAL_DAYS,
      stepInDay: indexWithinDay + 1,
      stepsInDay: dayScenarios.length,
      overallStep: state.scenarioIndex + 1,
      overallSteps: SCENARIOS.length,
    };
  }, [currentDay, currentScenario, state.scenarioIndex]);

  /** The player's primary run (matches selected mode). */
  const primaryRun = state.mode === "BCH" ? state.bch : state.btc;

  return {
    phase: state.phase,
    mode: state.mode,
    currentScenario,
    currentDay,
    progress,
    bch: state.bch,
    btc: state.btc,
    primaryRun,
    markets,
    priceStatus,
    start,
    restart,
    choose,
  };
}
